import { useCallback, useMemo, useRef, useState } from 'react';

import Avatar from '@/components/Avatar';
import LoadingState from '@/components/LoadingState';
import { Heading, Subheading } from '@/components/Typography';

import {
  useAddToAllowlist,
  useCommsAllowlist,
  useRemoveFromAllowlist,
  type AllowlistEntry,
} from '../api/useCommsAllowlist';
import { useWorkerSearch } from '../api/useWorkerSearch';
import { invalidateWorkerChips, type WorkerChip } from '../api/useWorkersByIds';

const pageStyle: React.CSSProperties = {
  padding: '20px 24px',
  maxWidth: 860,
  margin: '0 auto',
  width: '100%',
};

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  overflow: 'hidden',
};

const searchWrapStyle: React.CSSProperties = {
  position: 'relative',
  marginBottom: 20,
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 14,
  outline: 'none',
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
  maxHeight: 320,
  overflowY: 'auto',
  zIndex: 20,
};

const resultRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  cursor: 'pointer',
  borderBottom: '1px solid #f3f4f6',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderBottom: '1px solid #f3f4f6',
};

const removeButtonStyle: React.CSSProperties = {
  border: '1px solid #fecaca',
  background: '#fff',
  color: '#b91c1c',
  borderRadius: 6,
  padding: '6px 12px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const errorBannerStyle: React.CSSProperties = {
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#b91c1c',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 13,
  marginBottom: 16,
};

const mutedStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: 13,
};

function entryName(entry: AllowlistEntry): string {
  return entry.name?.trim() || entry.email || `User #${entry.user_id}`;
}

function searchResultName(worker: WorkerChip): string {
  const first = worker.user?.first_name ?? '';
  const last = worker.user?.last_name ?? '';
  const composed = `${first} ${last}`.trim();
  return composed || worker.user?.email || 'Worker';
}

function formatEnabledAt(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function CommsCohort() {
  const { entries, loading, error, refetch } = useCommsAllowlist();
  const { add, adding, error: addError } = useAddToAllowlist();
  const { remove, removingId, error: removeError } = useRemoveFromAllowlist();

  const [searchQuery, setSearchQuery] = useState('');
  const { results, loading: searchLoading } = useWorkerSearch(searchQuery);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const enabledUserIds = useMemo(
    () => new Set(entries.map((e) => e.user_id)),
    [entries],
  );

  const isSearching = searchQuery.trim().length >= 2;

  // Only workers with a linked user can be allowlisted — the allowlist is
  // keyed by user_id.
  const pickableResults = useMemo(
    () => results.filter((w) => w.user != null),
    [results],
  );

  const handleAdd = useCallback(
    async (worker: WorkerChip) => {
      const userId = worker.user?.id;
      if (userId == null) return;
      try {
        await add(userId);
        // The worker's comms_backend just changed — evict its cached chip so
        // the chat list refetches and its PubNub/Supabase badge updates without
        // a hard refresh.
        invalidateWorkerChips([worker.id]);
        setSearchQuery('');
        inputRef.current?.blur();
        await refetch();
      } catch {
        /* error surfaced via addError */
      }
    },
    [add, refetch],
  );

  // Removal is non-destructive — a worker can be re-added at any time — so we
  // remove immediately on click, mirroring the add flow (no confirmation).
  const handleRemove = useCallback(
    async (entry: AllowlistEntry) => {
      try {
        await remove(entry.user_id);
        // Worker dropped back to PubNub — evict its cached chip so the chat
        // list badge reverts without a hard refresh.
        if (entry.worker_id != null) invalidateWorkerChips([entry.worker_id]);
        await refetch();
      } catch {
        /* error surfaced via removeError */
      }
    },
    [remove, refetch],
  );

  const mutationError = addError ?? removeError;

  return (
    <div style={pageStyle}>
      <Heading>Chat V2 Cohort</Heading>
      <div style={{ ...mutedStyle, margin: '6px 0 20px' }}>
        Workers on this list use the new Supabase comms backend. Everyone else
        stays on PubNub.
      </div>

      <div style={searchWrapStyle}>
        <input
          ref={inputRef}
          type="search"
          placeholder="Search workers by name or email to add…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={searchInputStyle}
          disabled={adding}
        />
        {isSearching ? (
          <div style={dropdownStyle}>
            {searchLoading ? (
              <div style={{ padding: 12 }}>
                <LoadingState />
              </div>
            ) : pickableResults.length === 0 ? (
              <div style={{ padding: 14, ...mutedStyle }}>
                No matching workers.
              </div>
            ) : (
              pickableResults.map((worker) => {
                const alreadyAdded = enabledUserIds.has(worker.user!.id);
                return (
                  <div
                    key={worker.id}
                    onClick={
                      alreadyAdded || adding
                        ? undefined
                        : () => handleAdd(worker)
                    }
                    style={{
                      ...resultRowStyle,
                      cursor: alreadyAdded || adding ? 'default' : 'pointer',
                      opacity: alreadyAdded ? 0.55 : 1,
                    }}
                  >
                    <Avatar
                      firstName={searchResultName(worker)}
                      src={worker.avatar_url ?? undefined}
                      size="sm"
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>
                        {searchResultName(worker)}
                      </div>
                      <div style={mutedStyle}>{worker.user?.email ?? ''}</div>
                    </div>
                    <span style={{ ...mutedStyle, fontWeight: 600 }}>
                      {alreadyAdded ? 'Added' : adding ? 'Adding…' : 'Add'}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        ) : null}
      </div>

      {mutationError ? (
        <div style={errorBannerStyle}>{mutationError}</div>
      ) : null}

      <div style={cardStyle}>
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Subheading>Current cohort</Subheading>
          <span style={mutedStyle}>
            {entries.length} {entries.length === 1 ? 'worker' : 'workers'}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: 24 }}>
            <LoadingState />
          </div>
        ) : error ? (
          <div style={{ padding: 16 }}>
            <div style={errorBannerStyle}>{error}</div>
            <button
              type="button"
              onClick={() => void refetch()}
              style={{
                ...removeButtonStyle,
                border: '1px solid #d1d5db',
                color: '#374151',
              }}
            >
              Retry
            </button>
          </div>
        ) : entries.length === 0 ? (
          <div style={{ padding: 24, ...mutedStyle }}>
            No workers on the Supabase cohort yet. Use the search above to add
            one.
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry.user_id} style={rowStyle}>
              <Avatar
                firstName={entryName(entry)}
                src={entry.avatar_url ?? undefined}
                size="sm"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#111827',
                  }}
                >
                  {entryName(entry)}
                </div>
                <div style={mutedStyle}>{entry.email ?? ''}</div>
              </div>
              <div style={{ ...mutedStyle, whiteSpace: 'nowrap' }}>
                Added {formatEnabledAt(entry.enabled_at)}
              </div>
              <button
                type="button"
                onClick={() => void handleRemove(entry)}
                disabled={removingId === entry.user_id}
                style={{
                  ...removeButtonStyle,
                  opacity: removingId === entry.user_id ? 0.6 : 1,
                }}
              >
                {removingId === entry.user_id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default CommsCohort;
